import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Collapse from "react-bootstrap/Collapse";
import Select from "react-select";
import RequestServies from "../../../services/RequestServies";
import AddMrvForm from "./AddMrvForm";
import Swal from "sweetalert2";

const ALLOWED_EXCESS_PARTS = [
  "20634",
  "20635",
  "20636",
  "20637",
  "20638",
  "20639",
  "21766",
  "23470",
  "23471",
  "23472",
];

const MONEY_FIELDS = [
  "cgstAmountAtReceipt",
  "sgstAmountAtReceipt",
  "igstAmountAtReceipt",
  "amountWithGSTAtReceipt",
];

const getId = (value) => {
  if (!value) return "";
  return String(value?._id || value);
};

const isEmpty = (value) =>
  value === "" || value === undefined || value === null;

const hasDifferentBillUnit = (item) => {
  const inventoryUnitId = getId(item.inventoryUnitId);
  const billUnitId = getId(item.billUnitAtReceipt);

  return Boolean(
    inventoryUnitId &&
      billUnitId &&
      inventoryUnitId !== billUnitId
  );
};

const createEmptyItem = (emptyAmounts = false) => {
  const amount = emptyAmounts ? "" : 0;

  return {
    name: "",
    id: "",
    partNo: "",
    description: "",
    approveQty: 0,
    qtyRequired: 0,

    recivedQty: "",
    damageQty: 0,
    error: "",
    remark: "",

    unit: "",
    inventoryUnitId: "",
    poUnit: "",
    poUnitId: "",

    poNumber: "",
    poType: "",
    supplierName: "",

    billUnitAtReceipt: "",
    billUnitSameAsInventory: true,

    // Actual quantity shown on the bill.
    // Required only when bill and inventory units differ.
    billQtyAtReceipt: "",

    rateAtReceipt: amount,

    cgstAtReceipt: 0,
    sgstAtReceipt: 0,
    igstAtReceipt: 0,

    cgstAmountAtReceipt: amount,
    sgstAmountAtReceipt: amount,
    igstAmountAtReceipt: amount,
    amountWithGSTAtReceipt: amount,
  };
};

const getMaximumQty = (item) => {
  const approvedQty = Number(item.approveQty || 0);

  return ALLOWED_EXCESS_PARTS.includes(String(item.partNo))
    ? approvedQty * 1.05
    : approvedQty;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const Orders = () => {
  const [open, setOpen] = useState(true);
  const [open2, setOpen2] = useState(true);
  const [isModelOpen, setIsModelOpen] = useState(false);

  const user = JSON.parse(localStorage.getItem("userDetails"));
  const availableStores = user?.data?.user?.store || [];

  const [stores] = useState(availableStores);
  const [selectedStore, setSelectedStore] = useState(
    availableStores[0]?._id || ""
  );

  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [mrv, setMrv] = useState([]);
  const [selectedMrv, setSelectedMrv] = useState("");
  const [run, setRun] = useState(0);

  const [items, setItems] = useState([createEmptyItem()]);
  const [partOptions, setPartOptions] = useState([]);

  const showError = (message) =>
    Swal.fire({
      icon: "error",
      title: "Validation Error",
      text: message,
      confirmButtonText: "OK",
    });

  // ======================================
  // ROW VALIDATION
  // ======================================

  const validateItem = (item, index, adding = false) => {
    const row = `Row ${index + 1}`;
    const isFOC = item.poType === "FOC PO";

    if (!item.id || (adding && !item.name)) {
      return `${row}: Please select a part.`;
    }

    if (isEmpty(item.recivedQty)) {
      return `${row}: Please enter Received Qty.`;
    }

    const receivedQty = Number(item.recivedQty);

    if (!Number.isFinite(receivedQty) || receivedQty <= 0) {
      return `${row}: Received Qty must be greater than zero.`;
    }

    if (adding && item.error) {
      return `${row}: ${item.error}`;
    }

    if (!adding && receivedQty > getMaximumQty(item)) {
      return `${row}: Received Qty cannot exceed ${getMaximumQty(item)} ${
        item.unit || ""
      }.`;
    }

    if (!getId(item.billUnitAtReceipt)) {
      return `${row}: Please select Bill Unit.`;
    }

    // New validation: independent bill quantity.
    if (hasDifferentBillUnit(item)) {
      if (isEmpty(item.billQtyAtReceipt)) {
        return `${row}: Please enter Bill Qty in ${
          item.poUnit || "the selected bill unit"
        }.`;
      }

      const billQty = Number(item.billQtyAtReceipt);

      if (!Number.isFinite(billQty) || billQty <= 0) {
        return `${row}: Bill Qty must be greater than zero.`;
      }
    }

    if (isEmpty(item.rateAtReceipt)) {
      return `${row}: Please enter Rate.`;
    }

    const rate = Number(item.rateAtReceipt);

    if (!Number.isFinite(rate) || rate < 0) {
      return `${row}: Rate must be a valid non-negative number.`;
    }

    if (!isFOC && rate <= 0) {
      return `${row}: Rate must be greater than zero.`;
    }

    for (const field of MONEY_FIELDS) {
      if (isEmpty(item[field])) {
        return `${row}: Please enter all GST amounts and Total Amount.`;
      }

      const value = Number(item[field]);

      if (!Number.isFinite(value) || value < 0) {
        return `${row}: GST amounts and Total Amount must be valid non-negative numbers.`;
      }
    }

    const cgst = Number(item.cgstAmountAtReceipt);
    const sgst = Number(item.sgstAmountAtReceipt);
    const igst = Number(item.igstAmountAtReceipt);
    const total = Number(item.amountWithGSTAtReceipt);

    if (igst > 0 && (cgst > 0 || sgst > 0)) {
      return `${row}: Enter either IGST or CGST and SGST.`;
    }

    if ((cgst > 0 && sgst <= 0) || (sgst > 0 && cgst <= 0)) {
      return `${row}: CGST and SGST must both be entered.`;
    }

    if (cgst.toFixed(2) !== sgst.toFixed(2)) {
      return `${row}: CGST and SGST amounts must be equal.`;
    }

    // Existing Add Item validation.
    if (adding && !isFOC && cgst === 0 && sgst === 0 && igst === 0) {
      return `${row}: Please enter GST amount.`;
    }

    if (!isFOC && total <= 0) {
      return `${row}: Total Amount must be greater than zero.`;
    }

    // Existing submit validation.
    if (!adding && total < cgst + sgst + igst) {
      return `${row}: Total Amount cannot be less than total GST.`;
    }

    if (
      isFOC &&
      (rate !== 0 || cgst !== 0 || sgst !== 0 || igst !== 0 || total !== 0)
    ) {
      return `${row}: Rate, GST and Total Amount must be zero for FOC PO.`;
    }

    if (!String(item.remark || "").trim()) {
      return `${row}: Please enter Remark.`;
    }

    return "";
  };

  // ======================================
  // ADD / REMOVE ROW
  // ======================================

  const handleAddItem = (event) => {
    event.preventDefault();

    if (!items.length) {
      setItems([createEmptyItem(true)]);
      return;
    }

    const lastIndex = items.length - 1;
    const error = validateItem(items[lastIndex], lastIndex, true);

    if (error) {
      showError(error);
      return;
    }

    setItems((previous) => [
      ...previous,
      createEmptyItem(true),
    ]);
  };

  const handleRemoveItem = (event, index) => {
    event.preventDefault();

    setItems((previous) =>
      previous.filter((_, itemIndex) => itemIndex !== index)
    );
  };

  // ======================================
  // INPUT CHANGES
  // ======================================

  const handleItemChange = (index, field, value) => {
    setItems((previous) =>
      previous.map((existingItem, itemIndex) => {
        if (itemIndex !== index) return existingItem;

        const item = { ...existingItem };

        if (field === "damageQty") {
          item.damageQty = 0;
          return item;
        }

        const numericFields = [
          "recivedQty",
          "billQtyAtReceipt",
          "rateAtReceipt",
          ...MONEY_FIELDS,
        ];

        const nonNegativeFields = [
          "billQtyAtReceipt",
          "rateAtReceipt",
          ...MONEY_FIELDS,
        ];

        if (numericFields.includes(field)) {
          const numericValue = value === "" ? "" : Number(value);

          if (
            numericValue !== "" &&
            nonNegativeFields.includes(field) &&
            numericValue < 0
          ) {
            return existingItem;
          }

          item[field] = numericValue;
        } else {
          item[field] = value;
        }

        // Existing CGST / SGST synchronization.
        if (
          field === "cgstAmountAtReceipt" ||
          field === "sgstAmountAtReceipt"
        ) {
          const gstValue = value === "" ? "" : Number(value);

          item.cgstAmountAtReceipt = gstValue;
          item.sgstAmountAtReceipt = gstValue;

          if (Number(gstValue) > 0) {
            item.igstAmountAtReceipt = 0;
          }
        }

        if (
          field === "igstAmountAtReceipt" &&
          Number(value) > 0
        ) {
          item.cgstAmountAtReceipt = 0;
          item.sgstAmountAtReceipt = 0;
        }

        // Received Qty remains in inventory unit.
        if (field === "recivedQty") {
          item.damageQty = 0;

          if (value === "") {
            item.error = "Received Qty is required.";
          } else if (!Number.isFinite(Number(value))) {
            item.error = "Please enter a valid Received Qty.";
          } else if (Number(value) <= 0) {
            item.error = "Received Qty must be greater than zero.";
          } else if (Number(value) > getMaximumQty(item)) {
            item.error = `Received Qty cannot exceed ${getMaximumQty(
              item
            )} ${item.unit || ""}.`;
          } else {
            item.error = "";
          }
        }

        return item;
      })
    );
  };

  // ======================================
  // BILL UNIT CHANGE
  // ======================================

  const handleBillUnitChange = (index, selectedOption) => {
    if (!selectedOption) return;

    setItems((previous) =>
      previous.map((item, itemIndex) => {
        if (itemIndex !== index) return item;

        const billUnitId = getId(selectedOption.value);
        const inventoryUnitId = getId(item.inventoryUnitId);
        const unitChanged =
          billUnitId !== getId(item.billUnitAtReceipt);

        return {
          ...item,

          billUnitAtReceipt: billUnitId,

          billUnitSameAsInventory:
            billUnitId === inventoryUnitId,

          // Clear old bill quantity when switching units.
          billQtyAtReceipt: unitChanged
            ? ""
            : item.billQtyAtReceipt,

          // Received Qty, Rate, GST and Amount stay as entered.
        };
      })
    );
  };

  // ======================================
  // PART SELECTION
  // ======================================

  const handlePartSelect = async (index, selectedOption) => {
    if (!selectedOption) return;

    const isDuplicate = items.some(
      (item, itemIndex) =>
        itemIndex !== index &&
        getId(item.id) === getId(selectedOption.value)
    );

    if (isDuplicate) {
      await Swal.fire({
        icon: "error",
        title: "Duplicate Item",
        text: "This item has already been added.",
        confirmButtonText: "OK",
      });
      return;
    }

    const existingPOData = items
      .filter(
        (item, itemIndex) =>
          itemIndex !== index &&
          item.id &&
          !isEmpty(item.poNumber)
      )
      .map((item) => ({
        poNumber: String(item.poNumber),
        supplierName: item.supplierName || "N/A",
      }));

    const existingPONumbers = [
      ...new Set(existingPOData.map((item) => item.poNumber)),
    ];

    const selectedPONumber = isEmpty(selectedOption.poNumber)
      ? ""
      : String(selectedOption.poNumber);

    const selectedSupplierName =
      selectedOption.supplierName || "N/A";

    const isDifferentPO =
      existingPONumbers.length > 0 &&
      selectedPONumber &&
      !existingPONumbers.includes(selectedPONumber);

    if (isDifferentPO) {
      const uniqueExisting = [
        ...new Map(
          existingPOData.map((item) => [
            `${item.poNumber}-${item.supplierName}`,
            item,
          ])
        ).values(),
      ];

      const existingDetailsHtml = uniqueExisting
        .map(
          (item) => `
            <div style="
              border:1px solid #dee2e6;
              border-radius:6px;
              padding:8px 10px;
              margin-bottom:6px;
              background:#f8f9fa;
            ">
              <div>
                <strong>PO No.:</strong>
                ${escapeHtml(item.poNumber)}
              </div>
              <div>
                <strong>Supplier:</strong>
                ${escapeHtml(item.supplierName)}
              </div>
            </div>
          `
        )
        .join("");

      const result = await Swal.fire({
        icon: "warning",
        title: "Different PO Selected",
        html: `
          <div style="text-align:left">
            <p><strong>Existing selected item:</strong></p>
            ${existingDetailsHtml}
            <p><strong>New selected item:</strong></p>
            <div style="
              border:1px solid #ffc107;
              border-radius:6px;
              padding:8px 10px;
              background:#fff8e1;
            ">
              <div>
                <strong>PO No.:</strong>
                ${escapeHtml(selectedPONumber || "N/A")}
              </div>
              <div>
                <strong>Supplier:</strong>
                ${escapeHtml(selectedSupplierName)}
              </div>
            </div>
            <p style="margin-top:14px">
              Do you want to add this item from a different PO?
            </p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "Yes, Continue",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        focusCancel: true,
      });

      if (!result.isConfirmed) return;
    }

    setItems((previous) =>
      previous.map((item, itemIndex) => {
        if (itemIndex !== index) return item;

        const inventoryUnitId = getId(
          selectedOption.inventoryUnitId
        );

        return {
          ...item,

          id: selectedOption.value,
          partNo: selectedOption.partNo,
          name: selectedOption.label,
          description: selectedOption.description,

          approveQty: selectedOption.approveQty,
          qtyRequired: selectedOption.qtyRequired,

          unit: selectedOption.unit || "",
          inventoryUnitId,

          poUnit: selectedOption.poUnit || "",
          poUnitId: getId(selectedOption.poUnitId),

          poNumber: selectedOption.poNumber,
          poType: selectedOption.poType,
          supplierName: selectedOption.supplierName,

          recivedQty: "",
          damageQty: 0,
          error: "",
          remark: "",

          billUnitAtReceipt: inventoryUnitId,
          billUnitSameAsInventory: true,
          billQtyAtReceipt: "",

          rateAtReceipt: 0,

          cgstAtReceipt: 0,
          sgstAtReceipt: 0,
          igstAtReceipt: 0,

          cgstAmountAtReceipt: 0,
          sgstAmountAtReceipt: 0,
          igstAmountAtReceipt: 0,
          amountWithGSTAtReceipt: 0,
        };
      })
    );
  };

  // ======================================
  // LOAD MRVS
  // ======================================

  useEffect(() => {
    let cancelled = false;

    setItems([createEmptyItem()]);
    setPartOptions([]);
    setSelectedMrv("");
    setSelectedSupplier("");
    setMrv([]);

    if (!selectedStore) return;

    RequestServies.getMrv(selectedStore)
      .then((res) => {
        if (cancelled) return;

        const records = res?.data?.data || [];

        const filtered = records.filter(
          (record) =>
            record?.supplier &&
            !String(record.supplier?.name || "")
              .toLowerCase()
              .startsWith("local")
        );

        setMrv(filtered);

        if (filtered.length) {
          setSelectedMrv(getId(filtered[0]));
          setSelectedSupplier(getId(filtered[0].supplier));
        }
      })
      .catch((error) => {
        if (!cancelled) console.log(error);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedStore, run]);

  // ======================================
  // LOAD PARTS WITH MATCHING PO ROW
  // ======================================

  useEffect(() => {
    let cancelled = false;

    if (!selectedStore || !selectedSupplier || !selectedMrv) {
      setPartOptions([]);
      return;
    }

    RequestServies.getAllItem(
      undefined,
      "approved",
      selectedStore,
      "generated"
    )
      .then((res) => {
        if (cancelled) return;

        const records = res?.data?.data || [];

        const options = records
          .map((part) => {
            const masterItem = part?.inventory?.masterItem;
            const poItems = part?.po?.items || [];

            // Match this item, rather than always taking items[0].
            let poItem = poItems.find(
              (entry) => getId(entry.itemId) === getId(part)
            );

            if (!poItem) {
              const matches = poItems.filter(
                (entry) =>
                  String(entry.partNo) === String(masterItem?.partNo)
              );

              if (matches.length === 1) {
                poItem = matches[0];
              }
            }

            if (
              !poItem ||
              getId(poItem.supplier) !== getId(selectedSupplier)
            ) {
              return null;
            }

            return {
              value: part._id,
              label: `${masterItem?.partNo || ""} | ${
                masterItem?.description || ""
              }`,

              approveQty: part.approveQty,
              qtyRequired: part.qtyRequired,
              description: masterItem?.description || "",
              partNo: masterItem?.partNo,

              unit:
                masterItem?.unit?.name ||
                masterItem?.unit?.symbol ||
                "",

              inventoryUnitId: getId(masterItem?.unit),

              poUnit:
                poItem?.unitId?.name ||
                poItem?.unitId?.symbol ||
                poItem?.unit ||
                "",

              poUnitId: getId(poItem?.unitId),

              poNumber: part?.po?.poNumber,
              poType: part?.po?.poType,
              supplierName: poItem?.supplier?.name || "",
            };
          })
          .filter(Boolean);

        setPartOptions(options);
      })
      .catch((error) => {
        if (cancelled) return;
        console.log(error);
        setPartOptions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedStore, selectedSupplier, selectedMrv, run]);

  // ======================================
  // STORE / MRV SELECTION
  // ======================================

  const handleStoreChange = (selectedOption) => {
    setSelectedStore(selectedOption?.value || "");
    setSelectedMrv("");
    setSelectedSupplier("");
    setPartOptions([]);
    setItems([createEmptyItem()]);
  };

  const handleMrv = (selectedOption) => {
    setSelectedMrv(selectedOption?.value || "");
    setSelectedSupplier(selectedOption?.supplierId || "");
    setPartOptions([]);
    setItems([createEmptyItem()]);
  };

  // ======================================
  // SUBMIT MRV
  // ======================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedMrv) {
      showError("Please select MRV.");
      return;
    }

    if (!items.length) {
      showError("Please add at least one item.");
      return;
    }

    const itemIds = items.map((item) => getId(item.id));

    if (new Set(itemIds).size !== itemIds.length) {
      showError("Duplicate items are not allowed.");
      return;
    }

    for (let index = 0; index < items.length; index += 1) {
      const error = validateItem(items[index], index);

      if (error) {
        showError(error);
        return;
      }
    }

    const requestItems = items.map((item) => {
      const differentBillUnit = hasDifferentBillUnit(item);

      return {
        ...item,

        recivedQty: Number(item.recivedQty),
        damageQty: 0,

        approveQty: Number(item.approveQty || 0),
        qtyRequired: Number(item.qtyRequired || 0),

        billUnitAtReceipt: getId(item.billUnitAtReceipt),
        billUnitSameAsInventory: !differentBillUnit,

        // Same-unit case: existing received quantity.
        // Different-unit case: actual bill quantity.
        billQtyAtReceipt: differentBillUnit
          ? Number(item.billQtyAtReceipt)
          : Number(item.recivedQty),

        rateAtReceipt: Number(item.rateAtReceipt),

        // Backend reads applicable GST percentages from the PO.
        cgstAtReceipt: 0,
        sgstAtReceipt: 0,
        igstAtReceipt: 0,

        cgstAmountAtReceipt: Number(item.cgstAmountAtReceipt),
        sgstAmountAtReceipt: Number(item.sgstAmountAtReceipt),
        igstAmountAtReceipt: Number(item.igstAmountAtReceipt),

        amountWithGSTAtReceipt: Number(
          item.amountWithGSTAtReceipt
        ),

        remark: String(item.remark || "").trim(),
      };
    });

    try {
      await RequestServies.submitMrv({
        mrvId: selectedMrv,
        items: requestItems,
      });

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Order has been Received Successfully.",
      });

      setItems([createEmptyItem(true)]);
      setRun((previous) => previous + 1);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text:
          error?.response?.data?.message ||
          "Error during submission.",
      });
    }
  };

  // ======================================
  // INPUT HELPERS
  // ======================================

  const numberInput = (
    item,
    index,
    field,
    { disabled = false, min = 0, required = false } = {}
  ) => (
    <input
      type="number"
      className="form-control"
      value={item[field] ?? ""}
      min={min}
      step="any"
      required={required}
      disabled={disabled}
      onWheel={(event) => event.currentTarget.blur()}
      onFocus={(event) => event.currentTarget.select()}
      onChange={(event) =>
        handleItemChange(index, field, event.target.value)
      }
    />
  );

  const mrvOptions = mrv.map((record) => ({
    value: getId(record),
    label: `${record.mrvNo} | ${
      record.supplier?.name || "N/A"
    } | Bill No: ${record.billingNo || "N/A"}`,
    supplierId: getId(record.supplier),
  }));

  return (
    <>
      <AddMrvForm
        show={isModelOpen}
        handleClose={() => setIsModelOpen(false)}
        store={stores}
        onSuccess={() => setRun((previous) => previous + 1)}
      />

      <div className="row">
        <div className="col-xl-12">
          <div className="filter cm-content-box box-primary">
            <div className="content-title">
              <div className="cpa">
                <i className="fa-sharp fa-solid fa-filter me-2" />
                Filter
              </div>

              <div className="tools">
                <Link
                  to="#"
                  className="SlideToolHeader"
                  onClick={(event) => {
                    event.preventDefault();
                    setOpen((previous) => !previous);
                  }}
                >
                  <i
                    className={`fas ${
                      open ? "fa-angle-up" : "fa-angle-down"
                    }`}
                  />
                </Link>
              </div>
            </div>

            <Collapse in={open}>
              <div className="cm-content-body form excerpt">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Store</label>

                      <Select
                        options={stores.map((store) => ({
                          value: store._id,
                          label: store.name,
                        }))}
                        value={
                          stores
                            .map((store) => ({
                              value: store._id,
                              label: store.name,
                            }))
                            .find(
                              (option) =>
                                getId(option.value) ===
                                getId(selectedStore)
                            ) || null
                        }
                        onChange={handleStoreChange}
                        placeholder="Search Store"
                        isSearchable
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">MRV</label>

                      <Select
                        options={mrvOptions}
                        value={
                          mrvOptions.find(
                            (option) =>
                              option.value === getId(selectedMrv)
                          ) || null
                        }
                        onChange={handleMrv}
                        placeholder="Select MRV"
                        noOptionsMessage={() => "No records found"}
                        isSearchable
                      />
                    </div>

                    <div className="col-md-4 mb-3 d-flex align-items-end">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setIsModelOpen(true)}
                      >
                        Generate New MRV
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Collapse>
          </div>

          <div className="filter cm-content-box box-primary">
            <div className="content-title">
              <div className="cpa">
                <i className="fa-solid fa-plus me-2" />
                Add MRV Item
              </div>

              <div className="tools">
                <Link
                  to="#"
                  className="SlideToolHeader"
                  onClick={(event) => {
                    event.preventDefault();
                    setOpen2((previous) => !previous);
                  }}
                >
                  <i
                    className={`fas ${
                      open2 ? "fa-angle-up" : "fa-angle-down"
                    }`}
                  />
                </Link>
              </div>
            </div>

            <Collapse in={open2}>
              <div className="cm-content-body form excerpt">
                <div className="card-body">
                  <div className="table-responsive">
                    <div className="content_wrapper dataTables_wrapper no-footer">
                      <form onSubmit={handleSubmit} noValidate>
                        {items.map((item, index) => {
                          const differentBillUnit =
                            hasDifferentBillUnit(item);

                          const inventoryUnitId = getId(
                            item.inventoryUnitId
                          );

                          const poUnitId = getId(item.poUnitId);

                          const billUnitOptions = inventoryUnitId
                            ? [
                                {
                                  value: inventoryUnitId,
                                  label: `Same as Inventory (${
                                    item.unit || "Unit"
                                  })`,
                                },
                              ]
                            : [];

                          if (
                            poUnitId &&
                            poUnitId !== inventoryUnitId
                          ) {
                            billUnitOptions.push({
                              value: poUnitId,
                              label: `${item.poUnit || "PO Unit"} (PO Unit)`,
                            });
                          }

                          return (
                            <div
                              className="row mb-4 pb-3 border-bottom"
                              key={index}
                            >
                              <div className="col-md-2 mb-3">
                                <label className="form-label">
                                  Part
                                </label>

                                <Select
                                  options={partOptions}
                                  value={
                                    partOptions.find(
                                      (option) =>
                                        getId(option.value) ===
                                        getId(item.id)
                                    ) || null
                                  }
                                  onChange={(option) =>
                                    handlePartSelect(index, option)
                                  }
                                  placeholder="Part"
                                  menuPortalTarget={document.body}
                                  filterOption={(option, input) =>
                                    option.label
                                      .toLowerCase()
                                      .includes(input.toLowerCase())
                                  }
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      minWidth: 90,
                                      fontSize: 12,
                                    }),
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                />
                              </div>

                              <div className="col-md-2 mb-3">
                                <label className="form-label">
                                  Description
                                </label>
                                <input
                                  className="form-control"
                                  value={item.description || ""}
                                  readOnly
                                />
                              </div>

                              <div className="col-md-2 mb-3">
                                <label className="form-label">
                                  Required Qty ({item.unit})
                                </label>
                                <input
                                  className="form-control"
                                  value={item.qtyRequired ?? ""}
                                  readOnly
                                />
                              </div>

                              <div className="col-md-2 mb-3">
                                <label className="form-label">
                                  Approved Qty ({item.unit})
                                </label>
                                <input
                                  className="form-control"
                                  value={item.approveQty ?? ""}
                                  readOnly
                                />
                              </div>

                              <div className="col-md-2 mb-3">
                                <label className="form-label">
                                  Received Qty ({item.unit})
                                </label>

                                {numberInput(
                                  item,
                                  index,
                                  "recivedQty",
                                  { min: 0, required: true }
                                )}

                                {item.error && (
                                  <small className="text-danger d-block">
                                    {item.error}
                                  </small>
                                )}

                                <small className="text-muted d-block">
                                  Inventory and remaining quantity use
                                  this value in {item.unit || "inventory unit"}.
                                </small>
                              </div>

                              <div className="col-md-2 mb-3">
                                <label className="form-label">
                                  Damage Qty ({item.unit})
                                  <small className="text-muted ms-1">
                                    Disabled
                                  </small>
                                </label>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={0}
                                  readOnly
                                  disabled
                                  tabIndex={-1}
                                />
                              </div>

                              <div className="col-md-3 mb-3">
                                <label className="form-label">
                                  PO Number
                                </label>
                                <input
                                  className="form-control"
                                  value={item.poNumber || ""}
                                  readOnly
                                />
                              </div>

                              <div className="col-md-3 mb-3">
                                <label className="form-label">
                                  Supplier
                                </label>
                                <input
                                  className="form-control"
                                  value={item.supplierName || ""}
                                  readOnly
                                />
                              </div>

                              <div className="col-md-3 mb-3">
                                <label className="form-label">
                                  Rate
                                  {item.id && (
                                    <small className="text-muted ms-1">
                                      / {differentBillUnit
                                        ? item.poUnit
                                        : item.unit}
                                    </small>
                                  )}
                                </label>
                                {numberInput(
                                  item,
                                  index,
                                  "rateAtReceipt",
                                  { required: true }
                                )}
                              </div>

                              <div className="col-md-3 mb-3">
                                <label className="form-label">
                                  Bill Unit
                                </label>

                                <Select
                                  options={billUnitOptions}
                                  value={
                                    billUnitOptions.find(
                                      (option) =>
                                        option.value ===
                                        getId(item.billUnitAtReceipt)
                                    ) || null
                                  }
                                  onChange={(option) =>
                                    handleBillUnitChange(index, option)
                                  }
                                  isDisabled={
                                    !item.id || !inventoryUnitId
                                  }
                                  isClearable={false}
                                  isSearchable
                                  menuPortalTarget={document.body}
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      minHeight: 31,
                                      fontSize: 12,
                                    }),
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                />

                                {item.id && (
                                  <small className="text-muted d-block">
                                    {differentBillUnit
                                      ? `Enter actual Bill Qty and rate in ${item.poUnit}.`
                                      : `Received Qty is used for billing in ${item.unit}.`}
                                  </small>
                                )}

                                {item.id && !poUnitId && (
                                  <small className="text-danger d-block">
                                    PO unit ID is unavailable. Check PO data.
                                  </small>
                                )}
                              </div>

                              {/* NEW: actual bill quantity */}
                              {differentBillUnit && (
                                <div className="col-md-2 mb-3">
                                  <label className="form-label">
                                    Bill Qty ({item.poUnit})
                                    <span className="text-danger">
                                      {" "}*
                                    </span>
                                  </label>

                                  {numberInput(
                                    item,
                                    index,
                                    "billQtyAtReceipt",
                                    { min: 0, required: true }
                                  )}

                                  <small className="text-muted d-block">
                                    Enter quantity printed on the bill.
                                    Amount and GST validation use this
                                    quantity. Inventory uses Received Qty.
                                  </small>
                                </div>
                              )}

                              <div className="col-md-2 mb-3">
                                <label className="form-label">
                                  CGST Amount
                                </label>
                                {numberInput(
                                  item,
                                  index,
                                  "cgstAmountAtReceipt",
                                  {
                                    required: true,
                                    disabled:
                                      Number(item.igstAmountAtReceipt) > 0,
                                  }
                                )}
                              </div>

                              <div className="col-md-2 mb-3">
                                <label className="form-label">
                                  SGST Amount
                                </label>
                                {numberInput(
                                  item,
                                  index,
                                  "sgstAmountAtReceipt",
                                  {
                                    required: true,
                                    disabled:
                                      Number(item.igstAmountAtReceipt) > 0,
                                  }
                                )}
                              </div>

                              <div className="col-md-2 mb-3">
                                <label className="form-label">
                                  IGST Amount
                                </label>
                                {numberInput(
                                  item,
                                  index,
                                  "igstAmountAtReceipt",
                                  {
                                    required: true,
                                    disabled:
                                      Number(item.cgstAmountAtReceipt) > 0 ||
                                      Number(item.sgstAmountAtReceipt) > 0,
                                  }
                                )}
                              </div>

                              <div className="col-md-2 mb-3">
                                <label className="form-label">
                                  Total Amount Including GST
                                </label>
                                {numberInput(
                                  item,
                                  index,
                                  "amountWithGSTAtReceipt",
                                  { required: true }
                                )}
                              </div>

                              <div className="col-md-2 mb-3">
                                <label className="form-label">
                                  Remark
                                </label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={item.remark || ""}
                                  required
                                  onChange={(event) =>
                                    handleItemChange(
                                      index,
                                      "remark",
                                      event.target.value
                                    )
                                  }
                                />
                              </div>

                              <div
                                className="col-md-1 mb-3 d-flex align-items-start"
                                style={{ paddingTop: 30 }}
                              >
                                <button
                                  type="button"
                                  className="btn btn-danger btn-xs sharp"
                                  onClick={(event) =>
                                    handleRemoveItem(event, index)
                                  }
                                  aria-label="Remove item"
                                >
                                  <i className="fa fa-trash" />
                                </button>
                              </div>
                            </div>
                          );
                        })}

                        <div className="row">
                          <div className="col-md-2 mb-3">
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={handleAddItem}
                            >
                              Add Item
                            </button>
                          </div>

                          <div className="col-md-2 mb-3">
                            <button
                              type="submit"
                              className="btn btn-sm btn-primary"
                            >
                              Submit
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </Collapse>
          </div>
        </div>
      </div>
    </>
  );
};

export default Orders;